import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ShieldCheck, ShieldAlert, Save, Trash2, Loader2, Info, Eye, EyeOff, Lock,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { pedirExclusao } from '@/lib/confirmarExclusao'

interface IntegracaoStatus {
  configurado: boolean
  ativo: boolean
  provider: string
  provider_label?: string
  usuario: string | null
  tem_senha: boolean
  tem_api_key: boolean
  atualizado_em: string | null
}

// Página: Bureaus de Crédito — lista TODOS os bureaus suportados (Serasa + SPC).
// Mantém o nome de arquivo `IntegracaoSerasa.tsx` por compat de import.
export default function IntegracaoSerasaPage() {
  const { data: integracoes, isLoading } = useQuery<IntegracaoStatus[]>({
    queryKey: ['bureaus-credito'],
    queryFn: async () => (await api.get('/financeiro/analise-credito/integracoes/listar')).data,
  })

  if (isLoading) {
    return (
      <div className="card card-body text-center py-12 text-ink-500">
        <Loader2 size={24} className="mx-auto mb-2 animate-spin" />
        <p className="text-sm">Carregando...</p>
      </div>
    )
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <ShieldCheck size={22} className="text-brand-600" /> Bureaus de Crédito
        </h1>
        <p className="text-sm text-ink-500 mt-1">
          Configure as credenciais de cada bureau para habilitar consulta de score dos clientes na Análise de Crédito.
          Senha e API key são criptografadas com Fernet antes de gravar.
        </p>
      </div>

      {(integracoes || []).map((b) => (
        <BureauCard key={b.provider} status={b} />
      ))}
    </div>
  )
}

function BureauCard({ status }: { status: IntegracaoStatus }) {
  const qc = useQueryClient()
  const { user } = useAuth()
  const isAdmin = user?.perfil === 'admin' || user?.perfil === 'super_admin'
  const provider = status.provider
  const label = status.provider_label || provider

  const [usuario, setUsuario] = useState('')
  const [senha, setSenha] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [ativo, setAtivo] = useState(true)
  const [verSenha, setVerSenha] = useState(false)
  const [verApiKey, setVerApiKey] = useState(false)
  const [expandido, setExpandido] = useState(!status.configurado)

  useEffect(() => {
    if (status.configurado) {
      setUsuario(status.usuario || '')
      setAtivo(status.ativo)
    }
  }, [status])

  const salvar = useMutation({
    mutationFn: async () => {
      const payload: any = { provider, usuario: usuario || null, ativo }
      if (senha.trim()) payload.senha = senha.trim()
      if (apiKey.trim()) payload.api_key = apiKey.trim()
      return (await api.put(`/financeiro/analise-credito/integracoes/${provider}`, payload)).data
    },
    onSuccess: () => {
      toast.success(`${label} salvo`)
      setSenha(''); setApiKey('')
      qc.invalidateQueries({ queryKey: ['bureaus-credito'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  const remover = useMutation({
    mutationFn: async () =>
      (await api.delete(`/financeiro/analise-credito/integracoes/${provider}`)).data,
    onSuccess: () => {
      toast.success(`${label} removido`)
      setUsuario(''); setSenha(''); setApiKey(''); setAtivo(true)
      qc.invalidateQueries({ queryKey: ['bureaus-credito'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao remover'),
  })

  function handleSalvar() {
    if (!isAdmin) return
    if (!usuario.trim()) { toast.error('Informe o usuário'); return }
    if (!status.configurado) {
      if (!senha.trim()) { toast.error('Informe a senha'); return }
      if (!apiKey.trim()) { toast.error('Informe a API key'); return }
    }
    salvar.mutate()
  }

  function handleRemover() {
    if (!isAdmin) return
    remover.mutate()
  }

  return (
    <div className="card card-body space-y-3">
      {/* Cabeçalho com status */}
      <button
        type="button"
        onClick={() => setExpandido(v => !v)}
        className="flex items-center gap-3 w-full text-left"
      >
        {status.configurado ? (
          status.ativo ? (
            <div className="bg-emerald-100 text-emerald-700 p-2 rounded-full shrink-0"><ShieldCheck size={18} /></div>
          ) : (
            <div className="bg-amber-100 text-amber-700 p-2 rounded-full shrink-0"><ShieldAlert size={18} /></div>
          )
        ) : (
          <div className="bg-ink-100 text-ink-600 p-2 rounded-full shrink-0"><Lock size={18} /></div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-semibold">{label}</div>
          <div className="text-xs text-ink-500">
            {status.configurado
              ? (status.ativo
                ? `Ativo · Usuário: ${status.usuario || '—'}`
                : 'Configurado, mas inativo')
              : 'Não configurado'}
          </div>
        </div>
        <div className="text-xs text-brand-600 shrink-0">{expandido ? 'Recolher' : 'Configurar →'}</div>
      </button>

      {expandido && (
        <div className="space-y-3 pt-3 border-t border-ink-100">
          {!isAdmin && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800 flex items-start gap-2">
              <Info size={14} className="shrink-0 mt-0.5" />
              <div>Modo leitura. Apenas administradores podem editar credenciais.</div>
            </div>
          )}

          <div>
            <label className="label">Usuário</label>
            <input className="input" value={usuario} onChange={e => setUsuario(e.target.value)}
              placeholder={provider === 'spc_brasil' ? 'Ex: empresa@spcbrasil' : 'Ex: empresa@serasa'}
              disabled={!isAdmin} />
          </div>

          <div>
            <label className="label">
              Senha
              {status.tem_senha && <span className="text-xs text-emerald-600 ml-2">✓ já configurada (deixe em branco para manter)</span>}
            </label>
            <div className="relative">
              <input
                type={verSenha ? 'text' : 'password'}
                className="input pr-10"
                value={senha} onChange={e => setSenha(e.target.value)}
                placeholder={status.tem_senha ? '••••••••••' : 'Informe a senha'}
                disabled={!isAdmin} />
              <button type="button" onClick={() => setVerSenha(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700">
                {verSenha ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="label">
              API Key
              {status.tem_api_key && <span className="text-xs text-emerald-600 ml-2">✓ já configurada (deixe em branco para manter)</span>}
            </label>
            <div className="relative">
              <input
                type={verApiKey ? 'text' : 'password'}
                className="input pr-10"
                value={apiKey} onChange={e => setApiKey(e.target.value)}
                placeholder={status.tem_api_key ? '••••••••••' : 'Informe a API key'}
                disabled={!isAdmin} />
              <button type="button" onClick={() => setVerApiKey(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700">
                {verApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" id={`ativo-${provider}`} checked={ativo} onChange={e => setAtivo(e.target.checked)}
              disabled={!isAdmin} className="w-4 h-4" />
            <label htmlFor={`ativo-${provider}`} className="text-sm cursor-pointer select-none">
              Integração ativa (permite consultas)
            </label>
          </div>

          {isAdmin && (
            <div className="flex justify-between gap-2 pt-2">
              {status.configurado ? (
                <button onClick={() => pedirExclusao({ descricao: `as credenciais ${label} desta empresa`, onConfirm: handleRemover })} disabled={remover.isPending} className="btn-ghost text-sm text-red-600 hover:bg-red-50">
                  {remover.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Remover
                </button>
              ) : <div />}
              <button onClick={handleSalvar} disabled={salvar.isPending} className="btn-primary">
                {salvar.isPending ? <><Loader2 size={14} className="animate-spin" /> Salvando...</> : <><Save size={14} /> Salvar</>}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
